import { useEffect, useRef, useState } from "react";

/** Below this width the video waits for `load`; matches the `lg:` breakpoint used across the site. */
const DESKTOP_QUERY = "(min-width: 1024px)";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
/** Start loading a below-the-fold video a screenful before it scrolls in. */
const LAZY_ROOT_MARGIN = "200px";

export function hexToRgba(hex, opacity) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return `rgba(0,0,0,${opacity})`;
  const r = Number.parseInt(result[1], 16);
  const g = Number.parseInt(result[2], 16);
  const b = Number.parseInt(result[3], 16);
  return `rgba(${r},${g},${b},${opacity})`;
}

/**
 * Parses a raw BACKGROUND_MEDIA JSON string from site_content.
 * Returns an object with optional video, image, and overlay fields.
 * Returns {} when the value is missing or invalid — callers should
 * apply their own fallbacks for video/image, and treat absent overlay
 * as "no overlay".
 */
export function parseBackgroundMedia(raw) {
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

/**
 * Media query hook that always starts at `false`, so the server render and the first client
 * render agree (the server has no window). The real value arrives in an effect.
 */
function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return;
    }

    const mediaQuery = window.matchMedia(query);
    const onChange = () => {
      setMatches(mediaQuery.matches);
    };

    onChange();
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", onChange);
      return () => mediaQuery.removeEventListener("change", onChange);
    }

    mediaQuery.addListener(onChange);
    return () => mediaQuery.removeListener(onChange);
  }, [query]);

  return matches;
}

/** True when the visitor asked the browser to save data (Data Saver / metered connection). */
function useSaveData() {
  const [saveData, setSaveData] = useState(false);

  useEffect(() => {
    if (typeof navigator === "undefined") return;
    setSaveData(Boolean(navigator.connection?.saveData));
  }, []);

  return saveData;
}

/**
 * True once the document has fired `load`, i.e. the HTML, CSS, JS and the images that were in
 * the markup have all arrived. Starts `false` so the server render and the first client render
 * agree; on a page that is already complete the effect flips it immediately.
 */
function usePageLoaded() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;
    if (document.readyState === "complete") {
      setLoaded(true);
      return;
    }
    const onLoad = () => setLoaded(true);
    window.addEventListener("load", onLoad);
    return () => window.removeEventListener("load", onLoad);
  }, []);

  return loaded;
}

/**
 * Reports when `ref` has scrolled near the viewport. `enabled === false` (the eager hero, or a
 * section with no video at all) short-circuits to true/false without ever creating an observer.
 */
function useNearViewport(ref, enabled) {
  const [near, setNear] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    const element = ref.current;
    if (!(element && typeof IntersectionObserver === "function")) {
      // No observer to lean on — behave like the old code and load right away.
      setNear(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNear(true);
          observer.disconnect();
        }
      },
      { rootMargin: LAZY_ROOT_MARGIN }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, enabled]);

  return near;
}

/**
 * Background layer for a section: an image (or plain overlay) that paints immediately, with an
 * optional video on top of it.
 *
 * The video is a progressive enhancement only. It is left out entirely on the server, when the
 * visitor prefers reduced motion, and when Data Saver is on. On a desktop it starts as soon as
 * it is allowed; on anything smaller it also waits for the page's `load` event, so a
 * multi-megabyte file never competes with the content the visitor came for. `eager` is for the
 * video the visitor lands on; everything else waits until it is nearly on screen as well.
 */
export default function BackgroundMedia({
  imageUrl,
  videoUrl,
  overlayColor = "#000000",
  overlayOpacity = 0,
  className = "",
  eager = false,
}) {
  const prefersReducedMotion = useMediaQuery(REDUCED_MOTION_QUERY);
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const saveData = useSaveData();
  const pageLoaded = usePageLoaded();
  const baseRef = useRef(null);
  const videoRef = useRef(null);

  const videoAllowed = Boolean(videoUrl) && !prefersReducedMotion && !saveData;
  const near = useNearViewport(baseRef, videoAllowed && !eager);
  // Desktop: the hero plays right away, other sections when they come into view.
  // Phones and tablets: the same, but never before the page has finished loading.
  const shouldRenderVideo = videoAllowed && (isDesktop || pageLoaded) && (eager || near);

  useEffect(() => {
    if (!(shouldRenderVideo && videoRef.current)) return;
    videoRef.current.muted = true;
    videoRef.current.defaultMuted = true;
    videoRef.current.setAttribute("muted", "");
  }, [shouldRenderVideo]);

  return (
    <>
      {/* Painted first and always present: the poster image, or just the section colour. */}
      <div
        ref={baseRef}
        className={className}
        style={
          imageUrl
            ? {
                backgroundImage: `url('${imageUrl}')`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }
            : undefined
        }
        aria-hidden="true"
        data-testid="background-image"
      />
      {shouldRenderVideo && (
        <video
          ref={videoRef}
          src={videoUrl}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={imageUrl || undefined}
          className={className}
        />
      )}
      {overlayOpacity > 0 && (
        <div
          className="absolute inset-0"
          style={{ backgroundColor: hexToRgba(overlayColor, overlayOpacity) }}
          aria-hidden="true"
          data-testid="background-overlay"
        />
      )}
    </>
  );
}
